import { ApolloError } from "@apollo/client";
import type { GraphQLFormattedError } from "graphql";
import i18n from "i18next";
import { userFacingMessageKeyForGraphqlAuthError } from "Utils/graphqlAuthErrors";

export type AnyError = Error | ApolloError | string;

const MAX_GRAPHQL_ERRORS_SHOWN = 3;

/**
 * Maps errors to user-visible strings only. Do not report to Sentry here —
 * use `useHandleError(..., sendToSentry)` or explicit `Sentry.captureException` at the call site.
 */
export const displayError = (error?: AnyError): string => {
    if (error instanceof ApolloError) {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
            return displayApolloGraphQLErrors(error.graphQLErrors);
        }
        if (error.networkError) {
            return displayNetworkError(error.networkError);
        }
        return error.message;
    }
    if (typeof error === "string") {
        return error;
    }
    return error ? error.message : "";
};

export const logError = (error: AnyError) => {
    console.error(error);
};

function displayNetworkError(networkError: Error): string {
    const msg = networkError.message;
    if (msg.includes("Failed to fetch")) {
        return i18n.t("error_network_unreachable");
    }

    let status: number | undefined;
    if (
        "statusCode" in networkError &&
        typeof (networkError as { statusCode?: number }).statusCode === "number"
    ) {
        status = (networkError as { statusCode: number }).statusCode;
    }
    if (status === undefined) {
        const m = msg.match(/Received status code (\d+)/);
        if (m) {
            status = parseInt(m[1], 10);
        }
    }

    if (
        status === 400 ||
        msg === "Response not successful: Received status code 400"
    ) {
        return i18n.t("bad_request");
    }
    if (status === 401) {
        return i18n.t("session_ended_relogin");
    }
    if (status === 403) {
        return i18n.t("error_403_content");
    }
    if (status === 404) {
        return i18n.t("error_404_content");
    }
    if (status !== undefined && status >= 500) {
        return i18n.t("error_server_unavailable");
    }
    return msg;
}

function displayApolloGraphQLErrors(
    graphQLErrors: readonly GraphQLFormattedError[],
): string {
    const parts = graphQLErrors
        .slice(0, MAX_GRAPHQL_ERRORS_SHOWN + 2)
        .map((err) => displayOneGraphqlError(err));
    const unique: string[] = [];
    for (const p of parts) {
        if (p && !unique.includes(p)) {
            unique.push(p);
        }
        if (unique.length >= MAX_GRAPHQL_ERRORS_SHOWN) {
            break;
        }
    }
    return unique.length > 0 ? unique.join(" ") : "";
}

function displayOneGraphqlError(
    err:
        | GraphQLFormattedError
        | { message: string; extensions?: { code?: string } },
): string {
    const message = err.message;
    if (message.includes("Permission Denied")) {
        return i18n.t("missing_permissions");
    }
    if (message.includes("Please enter valid credentials")) {
        return i18n.t("wrong_credentials");
    }
    const authKey = userFacingMessageKeyForGraphqlAuthError(err);
    if (authKey) {
        return i18n.t(authKey);
    }
    return message;
}
